"use client";
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import InvoiceModal from "./InvoiceModal";
import CreateInvoiceModal from "./CreateInvoiceModal";
import BankInfoModal from "./BankInfoModal";
import { IoIosAddCircleOutline } from "react-icons/io";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { REST_API_URL } from "../../api/config";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function InvoiceApp() {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "invoice_date",
    direction: "desc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  const [activeTab, setActiveTab] = useState("unpaid");
  const [refreshFlag, setRefreshFlag] = useState(0);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      : null;

  useEffect(() => {
    if (!token) {
      console.warn("No token found");
      return;
    }

    const fetchInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const endpoint =
          activeTab === "unpaid"
            ? `${API_BASE}/invoice/invoices-unpaid/`
            : `${API_BASE}/invoice/invoices-paid/`;

        const headers = buildHeaders(token);
        
        const res = await axios.get(endpoint, {
          headers,
        });

        const data = Array.isArray(res.data) ? res.data : [];
        setInvoices(data);
        setCurrentPage(1);
      } catch (err) {
        console.error(`❌ Failed to fetch ${activeTab} invoices:`, err);
        toast.error(`Failed to load ${activeTab} invoices`);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };

    fetchInvoices();
  }, [activeTab, token, refreshFlag]);

  const sortedInvoices = useMemo(() => {
    if (!invoices.length) return [];

    let filtered = invoices.filter((inv) => {
      if (!search.trim()) return true;

      const q = search.toLowerCase();
      const name = inv.customer_name?.toLowerCase() || "";
      const email = inv.customer_email?.toLowerCase() || "";
      const ig = inv.customer_instagram_username?.toLowerCase() || "";
      const number = inv.invoice_number?.toLowerCase() || "";

      return (
        name.includes(q) ||
        email.includes(q) ||
        ig.includes(q) ||
        number.includes(q)
      );
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        if (sortConfig.key.includes("date")) {
          const aTime = new Date(aVal).getTime();
          const bTime = new Date(bVal).getTime();
          return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [search, sortConfig, invoices]);

  const totalPages = Math.ceil(sortedInvoices.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const visibleInvoices = sortedInvoices.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
    setCurrentPage(1);
  };

  const handleChangePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleRowClick = async (inv) => {
    if (!inv?.id) return;
    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    try {
      setLoadingInvoice(true);
      const headers = buildHeaders(token);
      
      const res = await axios.get(`${API_BASE}/invoice/invoice/${inv.id}/`, {
        headers,
      });
      setSelectedInvoice(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch invoice detail:", err);
      toast.error("Failed to load invoice details.");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleCreateInvoiceClick = async () => {
    if (!token) {
      toast.error("Authorization token not found");
      return;
    }

    try {
      const headers = buildHeaders(token);
      
      const res = await axios.get(`${API_BASE}/invoice/bank-info/`, {
        headers,
      });
      const data = res.data || {};

      const isEmpty =
        !data.name_surname &&
        !data.company_name &&
        !data.tax_id &&
        !data.address &&
        !data.phone_number &&
        !data.email &&
        !data.iban &&
        !data.swift_bic;

      if (isEmpty) {
        toast.error(
          "Please fill in your bank info before creating an invoice."
        );
        setShowBankModal(true);
      } else {
        setShowCreateModal(true);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(
          "Please fill in your bank info before creating an invoice."
        );
        setShowBankModal(true);
      } else {
        console.error("❌ Failed to check bank info:", err);
        toast.error("Failed to check bank info.");
      }
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearch("");
    setSortConfig({ key: "invoice_date", direction: "desc" });
  };

  return (
    <div className="h-screen bg-[#f4f6fb] flex items-center justify-center p-4 md:p-3">
      <div className="w-full h-full bg-white rounded-3xl shadow-lg flex flex-col overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3 md:gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <input
              type="text"
              placeholder="Search invoices..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-100 transition-all duration-200"
              disabled={loadingInvoices}
            />
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
          </div>

          <div className="relative inline-flex bg-gray-100 rounded-full p-1 text-sm font-medium">
            <div
              className={`absolute top-1 bottom-1 w-[calc(50%-0.25rem)] rounded-full bg-indigo-600 transition-all duration-300 ease-in-out ${
                activeTab === "unpaid"
                  ? "left-0.5"
                  : "left-[calc(50%-0.125rem)]"
              }`}
            />

            <button
              onClick={() => handleTabChange("unpaid")}
              className={`relative z-10 rounded-full px-5 py-1.5 w-1/2 transition-colors duration-200 ${
                activeTab === "unpaid"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-900"
              } ${
                loadingInvoices
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              disabled={loadingInvoices}
            >
              <span className="flex items-center justify-center gap-2 w-full">
                Unpaid
                {activeTab === "unpaid" && loadingInvoices && (
                  <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
              </span>
            </button>

            <button
              onClick={() => handleTabChange("paid")}
              className={`relative z-10 rounded-full px-5 py-1.5 w-1/2 transition-colors duration-200 ${
                activeTab === "paid"
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-900"
              } ${
                loadingInvoices
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
              disabled={loadingInvoices}
            >
              <span className="flex items-center justify-center gap-2 w-full">
                Paid
                {activeTab === "paid" && loadingInvoices && (
                  <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
              </span>
            </button>
          </div>

          <div className="flex flex-wrap gap-2 md:gap-3 justify-end">
            <button
              onClick={handleCreateInvoiceClick}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              disabled={loadingInvoices}
            >
              <IoIosAddCircleOutline className="text-lg" />
              <span>Create invoice</span>
            </button>

            <button
              onClick={() => setShowBankModal(true)}
              className="inline-flex items-center justify-center rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              disabled={loadingInvoices}
            >
              Bank info
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-100">
            {loadingInvoices ? (
              <div className="flex flex-col justify-center items-center py-16 gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                <p className="text-gray-500 text-sm">
                  Loading {activeTab} invoices...
                </p>
              </div>
            ) : (
              <>
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs font-semibold text-gray-500 sticky top-0 z-10">
                    <tr>
                      <th
                        className="px-6 py-3 cursor-pointer whitespace-nowrap hover:bg-gray-100 transition-colors duration-150"
                        onClick={() => handleSort("customer_name")}
                      >
                        <div className="flex items-center gap-1">
                          NAME
                          {sortConfig.key === "customer_name" && (
                            <span className="text-indigo-600">
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </th>

                      <th className="px-6 py-3 whitespace-nowrap">
                        PAYMENT DUE
                      </th>

                      <th className="px-6 py-3 whitespace-nowrap">TOTAL</th>

                      <th
                        className="px-6 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                        onClick={() => handleSort("invoice_number")}
                      >
                        <div className="flex items-center gap-1">
                          INVOICE #
                          {sortConfig.key === "invoice_number" && (
                            <span className="text-indigo-600">
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </th>

                      <th
                        className="px-6 py-3 whitespace-nowrap cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                        onClick={() => handleSort("invoice_date")}
                      >
                        <div className="flex items-center gap-1">
                          INVOICE DATE
                          {sortConfig.key === "invoice_date" && (
                            <span className="text-indigo-600">
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {visibleInvoices.map((inv, index) => (
                      <motion.tr
                        key={inv.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-gray-50 cursor-pointer group"
                        onClick={() => handleRowClick(inv)}
                      >
                        <td className="px-6 py-4 text-gray-800">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium flex-shrink-0">
                              {(inv.customer_name?.[0] || "?").toUpperCase()}
                            </div>
                            <span className="font-medium">
                              {inv.customer_name || "—"}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 text-gray-600">
                          {inv.payment_due ? `${inv.payment_due} days` : "—"}
                        </td>

                        <td className="px-6 py-4 text-gray-900 font-semibold">
                          {typeof inv.calculated_total_price === "number"
                            ? `$${inv.calculated_total_price.toFixed(2)}`
                            : "$0.00"}
                        </td>

                        <td className="px-6 py-4 text-gray-600 font-mono">
                          {inv.invoice_number || "—"}
                        </td>

                        <td className="px-6 py-4 text-gray-600">
                          {formatDate(inv.invoice_date)}
                        </td>
                      </motion.tr>
                    ))}

                    {visibleInvoices.length === 0 && !loadingInvoices && (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center py-12 text-gray-400 text-sm"
                        >
                          {search
                            ? "No invoices match your search"
                            : `No ${activeTab} invoices found`}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {sortedInvoices.length > 0 && !loadingInvoices && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 mb-6 flex flex-col md:flex-row items-center justify-between text-xs text-gray-500 gap-3"
            >
              <div className="flex items-center gap-2">
                <span>Showing</span>

                <select
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs focus:outline-none focus:border-indigo-500 cursor-pointer"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>

                <span>
                  of {sortedInvoices.length} entries
                  <span className="ml-2 text-gray-400">
                    ({startIndex + 1}-
                    {Math.min(startIndex + rowsPerPage, sortedInvoices.length)})
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleChangePage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-xs hover:bg-gray-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ‹
                </button>

                {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs transition-all duration-200 ${
                        currentPage === pageNum
                          ? "bg-indigo-600 text-white shadow-sm"
                          : "border border-gray-200 text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <span className="px-2">...</span>
                )}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <button
                    onClick={() => handleChangePage(totalPages)}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all duration-200 ${
                      currentPage === totalPages
                        ? "bg-indigo-600 text-white"
                        : ""
                    }`}
                  >
                    {totalPages}
                  </button>
                )}

                <button
                  onClick={() => handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-xs hover:bg-gray-100 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  ›
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <InvoiceModal
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onInvoiceUpdated={(updatedInvoice) => {
          setRefreshFlag((v) => v + 1);
        }}
        loading={loadingInvoice}
      />

      {showCreateModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onCreateSuccess={() => {
            setShowCreateModal(false);
            setRefreshFlag((v) => v + 1);
            toast.success("Invoice created successfully");
          }}
        />
      )}

      {showBankModal && (
        <BankInfoModal
          onClose={() => setShowBankModal(false)}
          onSuccess={() => {
            setShowBankModal(false);
            toast.success("Bank info saved successfully");
          }}
        />
      )}
    </div>
  );
}